import React, { useCallback, useEffect, useState } from "react";

import {
  Grid,
  Button,
  Typography,
  Switch,
  Link as MUILink,
  Skeleton,
  CircularProgress,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  ChevronRight,
  WifiOffOutlined,
  WifiOutlined,
  DeleteOutline,
  AccessTime,
  EditOutlined,
  AccessAlarm,
  SensorDoorOutlined,
  SettingsRemote,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { Link, RouteComponentProps } from "react-router-dom";
import { ptBR } from "date-fns/locale";

import {
  deleteScene,
  fetchEnvironmentStatus,
  fetchSceneList,
  updateSceneStatus,
} from "libLegacy/api/dashboard";
import { DeviceStatus, Environment, Scene } from "libLegacy/api/models";
import CustomSnackbar from "components/libComponents/CustomSnackbar";

import { BlockStandard } from "components/admin/BlockStandard";
import { PageStandard } from "components/admin/PageStandard";
import { useToken, useUserContext } from "contexts/UserContext";
import { useSetError } from "contexts/ErrorContext";
import { SpaceSelector } from "components/admin/SpaceSelector";
import { AutomationSpace } from "api/automationTypes";
import { CONFIGURE_IR, CREATE_ENVIRONMENT } from "routers/routePaths";
import { DISPLAY_DEVICE_CREATION } from "featureSwitches/flagsList";

const useStyles = makeStyles()((theme) => ({
  greenBorder: {
    borderRadius: "12px",
    border: `1px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(2),
    flexWrap: "nowrap",
    minHeight: 0,
    minWidth: 0,
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(2),
  },
  buttonIconFix: {
    "& > .MuiButton-startIcon": {
      marginRight: 0.5,
    },
  },
  modalView: {
    minWidth: "min(75vw, 450px)",
  },
}));

const countEnviroments = (enviroments: Array<Environment> | undefined) => {
  if (enviroments === undefined) {
    return undefined;
  }
  let online = 0;
  let percOn = "0%";
  let offline = 0;
  let percOff = "0%";
  const total = enviroments.length || 0;

  if (total) {
    enviroments.forEach((enviroment) => {
      enviroment.devices &&
      enviroment.devices.every(
        (device) => device.status === DeviceStatus.online
      )
        ? online++
        : offline++;
    });

    percOff = (offline / total).toLocaleString(ptBR.code, { style: "percent" });
    percOn = (online / total).toLocaleString(ptBR.code, { style: "percent" });
  }

  return { online, offline, total, percOff, percOn };
};

const ALERT_MESSAGES = {
  editSuccess: "Edição de evento realizada com sucesso!",
} as const;

export const AutomationHome = ({ history, location }: RouteComponentProps) => {
  const { classes } = useStyles();
  const token = useToken();
  const { currentSpace } = useUserContext();
  const [scenes, setScenes] = useState<Array<Scene> | undefined>(undefined);
  const [scenesError, setScenesError] = useState(false);
  const [loadingToggle, setLoadingToggle] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [environmentCount, setEnvironmentCount] = useState(
    countEnviroments(undefined)
  );
  const [hasAlert, setHasAlert] = useState<"editSuccess" | false>(false);
  const setGlobalError = useSetError();
  const [isDeletingScene, setIsDeletingScene] = useState<string>("");

  useEffect(() => {
    const load = async () => {
      try {
        const sceneList = await fetchSceneList(token, currentSpace?.id || "");
        setScenes(sceneList);
        setScenesError(false);
      } catch (error) {
        setScenesError(true);
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      }

      try {
        const environmentList = await fetchEnvironmentStatus(
          token,
          currentSpace?.id || ""
        );
        const enviromentsTotal = countEnviroments(environmentList);
        setEnvironmentCount(enviromentsTotal);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      }
    };

    if (currentSpace && currentSpace.hasInfrared) {
      load();
    }
  }, [currentSpace, token, setGlobalError]);

  useEffect(() => {
    // @ts-expect-error custom state fields aren't mapped by ReactRouter
    if (location.state?.editSuccessful) {
      setHasAlert("editSuccess");
    }
  }, [location]);

  const handleSceneEnabling = async (checked, id) => {
    setLoadingToggle(id);
    try {
      await updateSceneStatus(token, currentSpace?.id || "", id, checked);
      const updatedScenes = scenes?.map((scene) => {
        if (scene.id === id) {
          scene.isActive = checked;
        }
        return scene;
      });
      setScenes(updatedScenes);
    } catch (e) {
      // @ts-expect-error
      setGlobalError(e.response?.data?.error);
    } finally {
      setLoadingToggle("");
    }
  };

  const handleSceneDeleting = async () => {
    if (isDeletingScene) {
      const sceneId = isDeletingScene;
      try {
        await deleteScene(token, currentSpace?.id || "", sceneId);
        const updatedScenes = scenes?.filter((scene) => scene.id !== sceneId);
        setScenes(updatedScenes);
      } catch (e) {
        // @ts-expect-error
        setGlobalError(e.response?.data?.error);
      } finally {
        setLoadingToggle("");
        setIsDeletingScene("");
      }
    }
  };

  const spaceHasAutomation = useCallback((space: AutomationSpace): boolean => {
    return space.hasInfrared;
  }, []);

  const handleNoSpaceAvailable = useCallback((): void => {
    history.push("/admin/no-automation");
  }, [history]);

  const handleErrorClose = () => {
    setHasAlert(false);
  };

  const handleAdd = () => {
    setShowModal(true);
  };

  return (
    <PageStandard title="Automação" onAdd={handleAdd}>
      <Grid item container sx={{ mb: 1 }}>
        <SpaceSelector
          filterSpaces={spaceHasAutomation}
          onNoSpaceAvailable={handleNoSpaceAvailable}
        />
      </Grid>
      <BlockStandard>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h3">Eventos automatizados</Typography>
        </Grid>
        <Grid container className={classes.cardGrid}>
          {/* Still loading */}
          {!scenes && (
            <>
              <Skeleton height={150} />
              <Skeleton />
            </>
          )}
          {/* Scenes loaded successfully, but the array is empty */}
          {!scenes?.length && !scenesError && (
            <Typography align="center" sx={{ gridColumn: "1 / span 2" }}>
              Você ainda não tem eventos.
            </Typography>
          )}
          {/* Success case */}
          {!!scenes?.length &&
            scenes.map(({ id, name, triggerTime, isActive }) => (
              <Grid
                item
                container
                direction="column"
                key={id}
                className={classes.greenBorder}
                rowGap={1}
              >
                <Typography variant="body2" color="primary.main">
                  {name}
                </Typography>
                <Grid item container alignItems="center">
                  <AccessTime color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="caption" justifyContent="center">
                    {triggerTime}
                  </Typography>
                </Grid>

                <Grid container item direction="row" alignItems="center">
                  <Typography
                    fontWeight="bold"
                    variant="caption"
                    textTransform="uppercase"
                  >
                    off
                  </Typography>
                  <Switch
                    checked={isActive}
                    onChange={({ target: { checked } }) =>
                      handleSceneEnabling(checked, id)
                    }
                  />
                  <Typography
                    fontWeight="bold"
                    variant="caption"
                    textTransform="uppercase"
                    color={isActive ? "primary" : "secondary"}
                  >
                    on
                  </Typography>
                  {loadingToggle === id && <CircularProgress size={18} />}
                </Grid>
                <Grid
                  item
                  container
                  direction="row"
                  justifyContent="space-between"
                >
                  <MUILink
                    to={`/admin/automation/edit/${id}`}
                    component={Link}
                    underline="none"
                    sx={{ display: "flex", alignItems: "center", mr: 1 }}
                  >
                    <EditOutlined
                      color="secondary"
                      fontSize="small"
                      sx={{ mr: 0.5 }}
                    />
                    <Typography
                      color="secondary.main"
                      variant="caption"
                      textTransform="uppercase"
                    >
                      Editar
                    </Typography>
                  </MUILink>
                  <Button
                    startIcon={<DeleteOutline color="error" />}
                    onClick={() => setIsDeletingScene(id)}
                    sx={{ p: 0, height: "auto", justifyContent: "flex-start" }}
                    className={classes.buttonIconFix}
                  >
                    <Typography
                      color="error.main"
                      variant="caption"
                      textTransform="uppercase"
                    >
                      Excluir
                    </Typography>
                  </Button>
                </Grid>
              </Grid>
            ))}
        </Grid>
      </BlockStandard>
      <BlockStandard title="Ambientes">
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid container className={classes.cardGrid}>
            <Grid
              item
              container
              direction="row"
              className={classes.greenBorder}
            >
              <Grid item container direction="column" sx={{ flex: 9 }}>
                <Typography variant="body1" component="span">
                  Desconectados
                </Typography>
                <Grid container item direction="row" alignItems="center">
                  <WifiOffOutlined sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    {!environmentCount ? (
                      <Skeleton width={100} />
                    ) : (
                      <>
                        {environmentCount.offline}
                        <Typography variant="body1" component="span">
                          {` (${environmentCount.percOff})`}
                        </Typography>
                      </>
                    )}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                item
                container
                alignItems="center"
                justifyContent="center"
                sx={{ flex: 1 }}
              >
                <IconButton
                  component={Link}
                  to="/admin/automation/environments/offline"
                >
                  <ChevronRight />
                </IconButton>
              </Grid>
            </Grid>
            <Grid
              item
              container
              direction="row"
              className={classes.greenBorder}
            >
              <Grid item container direction="column" sx={{ flex: 9 }}>
                <Typography variant="body1" component="span">
                  Conectados
                </Typography>
                <Grid container item direction="row" alignItems="center">
                  <WifiOutlined color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" component="span">
                    {!environmentCount ? (
                      <Skeleton width={100} />
                    ) : (
                      <>
                        {environmentCount.online}
                        <Typography variant="body1" component="span">
                          {` (${environmentCount.percOn})`}
                        </Typography>
                      </>
                    )}
                  </Typography>
                </Grid>
              </Grid>

              <Grid
                item
                container
                alignItems="center"
                justifyContent="center"
                sx={{ flex: 1 }}
              >
                <IconButton
                  component={Link}
                  to="/admin/automation/environments/online"
                >
                  <ChevronRight />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </BlockStandard>
      <Dialog open={!!isDeletingScene} onClose={() => setIsDeletingScene("")}>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir a automação?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            onClick={() => {
              setIsDeletingScene("");
            }}
          >
            Cancelar
          </Button>
          <Button color="error" onClick={handleSceneDeleting}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showModal} onClose={() => setShowModal(false)}>
        <Grid item container className={classes.modalView}>
          <List sx={{ width: "100%" }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => history.push("/admin/automation/edit/new")}
              >
                <ListItemIcon>
                  <AccessAlarm />
                </ListItemIcon>
                <ListItemText primary="Evento" />
              </ListItemButton>
            </ListItem>

            {DISPLAY_DEVICE_CREATION.getValue() && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => history.push(CONFIGURE_IR)}>
                  <ListItemIcon>
                    <SettingsRemote />
                  </ListItemIcon>
                  <ListItemText primary="Controle remoto universal" />
                </ListItemButton>
              </ListItem>
            )}

            <ListItem disablePadding>
              <ListItemButton onClick={() => history.push(CREATE_ENVIRONMENT)}>
                <ListItemIcon>
                  <SensorDoorOutlined />
                </ListItemIcon>
                <ListItemText primary="Ambiente" />
              </ListItemButton>
            </ListItem>
          </List>
        </Grid>
      </Dialog>

      <CustomSnackbar
        message={!!hasAlert ? ALERT_MESSAGES[hasAlert] : ""}
        severity={hasAlert === "editSuccess" ? "success" : "error"}
        open={!!hasAlert}
        handleClose={handleErrorClose}
      />
    </PageStandard>
  );
};
